import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from '../lib/offlineFirestore';
import { db } from '../config/firebase';

const CORE_COLLECTIONS = [
  'growers', 'products', 'productVarieties', 'categories', 'agreements',
  'drivers', 'vehicles', 'financialYears', 'formSettings', 'chambers', 'bins',
  'crates', 'inwards', 'lots', 'outwards', 'warehouses'
];

const FINANCE_COLLECTIONS = [
  'invoices', 'payments', 'expenses', 'stockMovements', 'crateReconciliations',
  'bankReconciliations', 'cheques', 'employeeFinanceRecords'
];

const hasFinancePermission = (permissions = []) => permissions.some((permission) => {
  const value = String(permission || '').toLowerCase();
  return value === 'all' || value.includes('finance') || value.includes('billing') || value.includes('invoice') || value.includes('payment');
});

const safeTenantQuery = async (name, tenantId, max = 1200) => {
  const seen = new Set();
  let count = 0;
  const tenantRef = doc(db, 'tenants', tenantId);
  const attempts = [
    query(collection(db, name), where('tenantIdValue', '==', tenantId), limit(max)),
    query(collection(db, name), where('companyIdValue', '==', tenantId), limit(max)),
    query(collection(db, name), where('tenantId', '==', tenantId), limit(max)),
    query(collection(db, name), where('companyId', '==', tenantId), limit(max)),
    query(collection(db, name), where('tenantId', '==', tenantRef), limit(max)),
    query(collection(db, name), where('companyId', '==', tenantRef), limit(max)),
  ];

  for (const attempt of attempts) {
    try {
      const snap = await getDocs(attempt);
      snap.docs.forEach((row) => seen.add(row.ref.path));
      count += snap.size;
      if (snap.size) break;
    } catch (_) {}
  }

  // Several newer modules store tenant data as subcollections. Pre-cache that
  // shape as well; failures are harmless for collections that are top-level.
  try {
    const nested = await getDocs(query(collection(db, 'tenants', tenantId, name), limit(max)));
    nested.docs.forEach((row) => seen.add(row.ref.path));
    count += nested.size;
  } catch (_) {}

  return { name, count: seen.size || count };
};

const offlinePrefetchService = {
  async prefetch({ tenantId, permissions = [], includeFinance = null, onProgress } = {}) {
    if (!tenantId) return { totalCollections: 0, totalDocuments: 0, results: [] };

    const names = [
      ...CORE_COLLECTIONS,
      ...((includeFinance ?? hasFinancePermission(permissions)) ? FINANCE_COLLECTIONS : []),
    ];

    // The tenant document contains company settings, workflow, print config,
    // mobile config and branding. Cache it first because most screens depend on it.
    try { await getDoc(doc(db, 'tenants', tenantId)); } catch (_) {}

    const results = [];
    for (let index = 0; index < names.length; index += 1) {
      const name = names[index];
      const result = await safeTenantQuery(name, tenantId).catch(() => ({ name, count: 0 }));
      results.push(result);
      if (typeof onProgress === 'function') {
        onProgress({ index: index + 1, total: names.length, name, count: result.count });
      }
    }

    return {
      totalCollections: results.length,
      totalDocuments: results.reduce((sum, row) => sum + Number(row.count || 0), 0),
      results,
      completedAt: new Date().toISOString(),
    };
  },
};

export default offlinePrefetchService;
